import { useState, useCallback, useMemo, useEffect, Suspense } from "react";
import { useLoaderData, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ListGroup from "react-bootstrap/ListGroup";
import { DatasetApi } from "./api/DatasetApi";
import { DotaV1Class } from "./ml/Dota";
import { ObbAnnotation } from "./api/Application";
import { aerialImageTileSpec, datasetImageUrl } from "./api/Url";
import { generateThumbnail, loadImageToCanvas, MemoItem } from "./common/Dataset";
import DeckGL from "@deck.gl/react";
import { OrthographicView, OrthographicViewState, PickingInfo } from "@deck.gl/core";
import { BitmapLayer, GeoJsonLayer, PolygonLayer, TextLayer } from "@deck.gl/layers";
import { EditVehicleModal } from "./DatasetImageAnnotationPageVehicleEditModal";
import { CommandCard, CommandCardSlot } from "./CommandCard";
import { VehicleStatsModal } from "./DatasetImageAnnotationPageStatsModal";
import { Form } from "react-bootstrap";
import { createVehiclePolygonNormalized } from "./common/Geometry";
import { PiExport } from "react-icons/pi";
import { Button } from "./components/Button";
import { DatasetImageLoaderData } from "./loaders/datasetImageLoader";
import { useOptionalParamIds, useParamIds } from "./api/ApiUtil";
import { createCreateMode } from "./components/Annotation/CreateMode";
import { createEditMode } from "./components/Annotation/EditMode";
import { getModeFns } from "./components/Annotation/getModeFns";
import { VehicleColor } from "./components/Annotation/VehicleColor";
import { createVehicleColorMode } from "./components/Annotation/VehicleColorMode";
import { showVehicleType, VehicleType } from "./components/Annotation/VehicleType";
import { createVehicleTypeMode } from "./components/Annotation/VehicleTypeMode";
import { DrawingState, Mode, DrawingModeContext } from "./components/Annotation/ModeContext";
import type { Feature, Geometry } from "geojson";
import { createPartMode } from "./components/Annotation/VehiclePartsMode";
import { createImageProjection } from "./common/Geography";
import { MjolnirEvent } from 'mjolnir.js';
import { classifyFromCanvas, ClassifyOutput, loadModel } from "./DatasetImageAnnotationPageWebGpu";
import { InferenceSession } from "onnxruntime-web";
import { AdjustableBitmapLayer, FilterMode, IMAGE_ADJUST_DEFAULTS, ImageAdjustPanel, ImageAdjustProps } from "./DatasetImageAnnotationPageShaders";
import { PatchedTextLayer } from "./DatasetImageAnnotationPageFontPanel";
import { TileLayer } from "deck.gl";
import proj4 from "proj4";
import { createExtrasMode, VehicleExtra } from "./components/Annotation/VehicleExtrasMode";
import { createParkingMode } from "./components/Annotation/ParkingMode";

export function DatasetImageObbAnnotationPage() {
    const nav = useNavigate();
    const loc = useLocation();    
    const [ searchParams, setSearchParams ] = useSearchParams();
    const { datasetId    } = useParamIds(); 
    const { annotationId } = useOptionalParamIds();    
    const [ image, annotations, aerialImage ] = useLoaderData() as DatasetImageLoaderData;

    const [ objs, setObjs] = useState<ObbAnnotation[]>( annotations );
    const selObj = useMemo( () => {
        return annotationId ? objs.find( x => x.obbAnnotationId === annotationId ) : undefined;
    }, [annotationId, objs] );
    const [ hovObj,       setHovObj       ] = useState<ObbAnnotation | undefined>();
    const [ drawMode,     setDrawMode     ] = useState<DrawingState>( { mode: Mode.Inactive } );
    const [ canvasData,   setCanvasData   ] = useState<{ canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }>();
    const [ thumbnails,   setThumbnails   ] = useState<Map<number, string>>( new Map() );
    const [ newPolygon,   setNewPolygon   ] = useState<[number, number][]>( [] );
    const [ showStats,    setShowStats    ] = useState<boolean>( false );
    const [ filterByMode, setFilterByMode ] = useState<boolean>( true );
    const [ render,       setRender       ] = useState<boolean>( true );    
    const [ model,        setModel        ] = useState<InferenceSession>();
    const [ predictions,  setPredictions  ] = useState<Map<number, ClassifyOutput>>();
    const [ adjust,       setAdjust       ] = useState<ImageAdjustProps>( IMAGE_ADJUST_DEFAULTS );

    const offset = useMemo( () => {
        return ( { 
            x: Number( searchParams.get( "x" ) ?? 0 ),
            y: Number( searchParams.get( "y" ) ?? 0 )
        } );
    }, [searchParams.get( "x" ), searchParams.get( "y" )] );
    const [showSupport, setShowSupport] = useState<boolean>( false );

    // useEffect( () => {
    //     loadModel().then( model => {
    //         return setModel( model );
    //     } );
    // }, [] );

    useEffect( () => {
        if( !canvasData ) { return; }
        if( !model      ) { return; }
        if( predictions ) { return; } 
        let cancelled = false;
        ( async () => {
            const map = new Map<number, ClassifyOutput>();
            for( const ann of objs ) {
                const r = await classifyFromCanvas( model, canvasData.canvas, ann, image.width, image.height, image.pixelWidth ?? 0.30, image.pixelHeight ?? 0.30  );
                if( cancelled ) { return; }
                map.set( ann.obbAnnotationId, r );
            }
            if( !cancelled ) { 
                setPredictions( map );
            }
        } )();
        return () => { cancelled = true; };
    }, [canvasData, model, predictions] );

    const projection = useMemo( () => {
        return createImageProjection( image );
    }, [image] );

    const openInGoogleMaps = useCallback( ( ann?: ObbAnnotation ) => {
        if( !projection ) { return; }
        const px = ann ? ann.x * image.width  : image.width  / 2;
        const py = ann ? ann.y * image.height : image.height / 2;
        const [lat, lng] = projection.pixelToLatLng( px, py );
        window.open(
            `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&zoom=21`,            
            "vehicle-earth-tab"
            //alternative urls
            // `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}&heading=90&pitch=5&fov=80`,
            // `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`,
            // `https://earth.google.com/web/@${lat},${lng},0a,500d,35y,0h,0t,0r`,            
        );
    }, [image, projection] );

    //we only change it on initial load, as from an external link
    const [viewState, setViewState] = useState<OrthographicViewState>( {
        target:  selObj ? [selObj.x * image.width, selObj.y * image.height] : [image.width / 2, image.height / 2, 0],
        zoom:    selObj ? 3 : 0,
        minZoom: -5,
        maxZoom:  5
    } );

    useEffect( () => {
        loadImageToCanvas( datasetImageUrl( image.datasetId, image.datasetImageId ) ).then( setCanvasData );
    }, [image.datasetImageId] );

    useEffect( () => {
        if( !canvasData ) { return; }
        const thumbs = new Map<number, string>();
        for( const ann of objs ) {
            const url = generateThumbnail( canvasData.canvas, ann, image.width, image.height );
            thumbs.set( ann.obbAnnotationId, url );
        }
        setThumbnails( thumbs );
    }, [canvasData, image] );

    const classify = useCallback( async ( ann: ObbAnnotation ) => {
        if( !canvasData || !model ) {  return; }
        const r = await classifyFromCanvas( model, canvasData.canvas, ann, image.width, image.height, image.pixelWidth ?? 0.30, image.pixelHeight ?? 0.30 );
        setPredictions( prev => new Map( prev ).set( ann.obbAnnotationId, r ) );
    }, [canvasData, model, image.width, image.height] );

    const regenerateThumbnail = useCallback( ( ann: ObbAnnotation ) => {
        if( !canvasData ) { return; }
        const url = generateThumbnail( canvasData.canvas, ann, image.width, image.height );
        setThumbnails( prev => new Map( prev ).set( ann.obbAnnotationId, url ) );
    }, [canvasData, image] );

    const handleAdd = useCallback( async ( ann: ObbAnnotation ) => {
        return DatasetApi.addAnnotation( image.datasetImageId, { ...ann } ).then( added => {
            setObjs( prev => [...prev, added] );
            regenerateThumbnail( added );
            classify( added );
        } );
    }, [image, regenerateThumbnail, classify] );

    const handleEdit = useCallback( async ( ann: ObbAnnotation ) => {
        return DatasetApi.updateAnnotation( ann.obbAnnotationId, ann ).then( updated => {
            setObjs( prev => prev.map( a => ( a.obbAnnotationId === ann.obbAnnotationId ? updated : a ) ) );
            regenerateThumbnail( updated );
            classify( ann );
        } );
    }, [regenerateThumbnail, classify] );

    const setSelObj = useCallback( ( ann: ObbAnnotation | undefined ) => {
        const path = ann
            ? `/dataset/${datasetId}/image/${image.datasetImageId}/${ann.obbAnnotationId}`
            : `/dataset/${datasetId}/image/${image.datasetImageId}`;
        nav( `${path}${loc.search}`, { replace: true } );
    }, [nav, datasetId, image.datasetImageId, loc.search] );

    const handleDelete = useCallback( async ( ann: ObbAnnotation ) => {
        return DatasetApi.deleteAnnotation( ann.obbAnnotationId ).then( () => {
            setObjs( prev => prev.filter( a => a.obbAnnotationId !== ann.obbAnnotationId ) );
            if( selObj?.obbAnnotationId === ann.obbAnnotationId ) {
                setSelObj( undefined );
            }
        } );
    }, [selObj, setSelObj] );

    const modeFns = getModeFns( drawMode );
    const ctx: DrawingModeContext = useMemo( () => ( {
        imageWidth:  image.width,
        imageHeight: image.height,
        selObj,
        hovObj,
        filterByMode,
        render,
        setDrawMode,
        setSelObj, 
        setNewPolygon,
        setFilterByMode, 
        setRender, 
        setShowStats,
        handleAdd, 
        handleEdit, 
        handleDelete,
    } ), [image.width, image.height, selObj, hovObj, filterByMode, render, setSelObj] );

    //filtering
    const filterObj = useCallback( ( x: ObbAnnotation ) => {
        return modeFns.filter( drawMode, x );
    }, [drawMode, modeFns] );

    const filtered = useMemo( () => {
        if( !filterByMode ) { return objs; }
        return objs.filter( filterObj );
    }, [objs, filterByMode, filterObj] );

    const detections = useMemo( () => ( {
        type:     "FeatureCollection" as const,
        features: objs.map( obj => {
            const pnts = createVehiclePolygonNormalized( image, obj );
            return { type: "Feature" as const, geometry: { type: "Polygon" as const, coordinates: [pnts] }, properties: obj };
        } )
    } ), [image, objs] );

    const polygons = useMemo( () => {
        return new GeoJsonLayer<ObbAnnotation>( {
            id:                 "annotations",
            data:               detections,
            lineWidthMinPixels: 2,
            getLineWidth:       0.50,
            stroked:            true,
            filled:             true,
            getFillColor:       [0, 0, 0, 0],
            getLineColor: ( d: Feature<Geometry, ObbAnnotation> ) => {
                if( !render ) { return [ 0, 0, 0, 0 ]; }
                return modeFns.getLineColor( drawMode, ( d.properties as ObbAnnotation ), ctx );
            },
            pickable: true,
            onClick: ( info: any ) => {
                const ann = info.object.properties as ObbAnnotation;
                modeFns.onAnnotationClick( drawMode, ann, ctx );
            },
            onHover: ( info ) => {
                const ann = info.object != null ? info.object.properties as ObbAnnotation : undefined;
                if( hovObj === ann ) { return; }
                setHovObj( ann );
            },
            updateTriggers: {
                getLineColor: [drawMode, modeFns, selObj, hovObj, render]
            }
        } );
    }, [detections, drawMode, modeFns, selObj, hovObj, render] );

    const typeLabels = useMemo( () => {
        return new PatchedTextLayer( {
            id:                     "type-labels",
            data:                   filtered,
            getPosition:            o => [o.x * image.width, o.y * image.height],
            getText:                o => modeFns.getAnnotationLabel( drawMode, o ),
            fontFamily:             "monospace",
            sizeUnits:              "common",
            getSize:                x => 4,
            sizeMinPixels:          12,
            getColor:               [  0,   0,   0, 255],
            fontWeight:             "bold",
            getTextAnchor:          "middle",
            getAlignmentBaseline:   "center",
            outlineWidth:           6,
            outlineColor:           [255, 255, 255, 255],
            fontSettings: {
                sdf:       true,
                buffer:    16,
                radius:    24,
                cutoff:    0.20,
                smoothing: 0.04,
            },
            updateTriggers: {
                getText: [drawMode, filterByMode],
                data:    [filtered]
            }
        } );
    }, [image, drawMode, modeFns, filtered, filterByMode] );

    useEffect( () => {
        if( drawMode?.mode === Mode.Create && drawMode.rearCenter === undefined ) {
            setNewPolygon( [] );
        }
    }, [drawMode] );

    const handleClick = useCallback( ( info: PickingInfo, event: MjolnirEvent ) => {
        if( event.srcEvent.shiftKey ) {
            openInGoogleMaps( info.object?.properties );
        }
        modeFns.onDeckClick( drawMode, info, ctx );        
    }, [drawMode, modeFns, ctx] );

    const handleMouseMove = useCallback( ( info: PickingInfo ) => {
        modeFns.onDeckHover( drawMode, info, ctx );
    }, [drawMode, modeFns, ctx] );

    useEffect( () => {
        function onKeyDown( ev: KeyboardEvent ) {
            const key   = ev.key.length === 1 ? ev.key.toLowerCase() : ev.key;
            const shift = ev.shiftKey;
            const ctrl  = ev.ctrlKey;
            if( selObj !== undefined ) {
                if( key === "Delete" || key === "Backspace" ) { handleDelete( selObj ); }
            }
            if( ev.key === "Escape" ) {
                setSelObj( undefined );
                setNewPolygon( [] );
                setDrawMode( { mode: Mode.Inactive } );
            }
            //view modifiers
            if( key === "u" ) { setViewState( { ...viewState, zoom: 0 } ); ev.stopPropagation(); }
            if( key === "i" ) { setViewState( { ...viewState, zoom: 1 } ); ev.stopPropagation(); }
            if( key === "o" ) { setViewState( { ...viewState, zoom: 2 } ); ev.stopPropagation(); }
            if( key === "p" ) {
                const modes: FilterMode[] = ["nearest", "linear", "lanczos"];
                const idx = modes.indexOf( adjust.filterMode );
                setAdjust( prev => ({ ...prev, filterMode: modes[(idx + 1) % 3] }) );
            }
            if( key === "m" ) {
                setShowSupport( !showSupport )
            }

            if( key === "`"  ) { setDrawMode( createEditMode()                                  ); }
            if( key === "1"  ) { setDrawMode( createCreateMode( DotaV1Class.SmallVehicle, 2.5 / ( image.pixelWidth ?? 0.3 ) ) ); }
            if( key === "2"  ) { setDrawMode( createVehicleTypeMode( VehicleType.Car          ) ); }
            if( key === "3"  ) { setDrawMode( createVehicleColorMode( VehicleColor.White      ) ); }
            if( key === "4"  ) { setDrawMode( createPartMode()                                  ); }
            if( key === "5"  ) { setDrawMode( createExtrasMode( VehicleExtra.Shade            ) ); }
            if( key === "6"  ) { setDrawMode( createParkingMode()                               ); }
            if( key === "y"  ) { setFilterByMode( !filterByMode );                                 }
            if( key === "l"  ) { setRender      ( !render );                                       }
                       
            const updateOffset = ( dx: number, dy: number ) => {
                setSearchParams( prev => {
                    const next = new URLSearchParams( prev );
                    next.set( "x", String( Number( prev.get( "x" ) ) + dx ) );
                    next.set( "y", String( Number( prev.get( "y" ) ) + dy ) );
                    return next;
                }, { replace: true } );
            };

            if( shift && key === "ArrowLeft"  ) { updateOffset( -1,  0 ); }
            if( shift && key === "ArrowRight" ) { updateOffset(  1,  0 ); }
            if( shift && key === "ArrowUp"    ) { updateOffset(  0, -1 ); }
            if( shift && key === "ArrowDown"  ) { updateOffset(  0,  1 ); }

            //delegate to active mode
            if( modeFns.onKeyDown( drawMode, key, shift, ctx ) ) {
                return;
            }
            if( key === "/" ) { setShowStats( true ); }
        }
        window.addEventListener( "keydown", onKeyDown );
        return () => window.removeEventListener( "keydown", onKeyDown );
    }, [drawMode, modeFns, selObj, setSelObj, filterByMode, render, adjust.filterMode, showSupport, offset] );

    const previewLayer = newPolygon.length ? new PolygonLayer( {
        id:                 "new-polygon",
        data:               [newPolygon],
        filled:             true,
        stroked:            true,
        getPolygon:         ( d ) => d,
        getFillColor:       [59, 130, 246, 80],
        getLineColor:       [59, 130, 246, 200],
        lineWidthMinPixels: 2,
        getLineWidth:       0.50,
    } ) : null;

    const editLayer = drawMode?.mode === Mode.Edit && drawMode.annotation !== undefined ? new PolygonLayer( {
        id:                 "edit-polygon",
        data:               [createVehiclePolygonNormalized( image, drawMode.annotation )],
        filled:             true,
        stroked:            true,
        getPolygon:         ( d ) => d,
        getFillColor:       [0, 0, 0, 0],
        getLineColor:       [0, 255, 0, 96],
        lineWidthMinPixels: 2,
        getLineWidth:       0.50,
    } ) : null;

    const imageLayer = useMemo( () => new AdjustableBitmapLayer( {
        id: "image",
        bounds: [0, image.height, image.width, 0],
        image: datasetImageUrl( image.datasetId, image.datasetImageId ),
        textureParameters: {
            minFilter: "nearest",
            magFilter: "nearest",
        },
        imageAdjust: adjust,
        imageWidth:  image.width,
        imageHeight: image.height,
    } ), [image, adjust] );

    const supportLayer = useMemo( () => {
        if( !aerialImage ) { return null; }
        if( !( image.maxX 
            && image.maxY 
            && image.originX
            && image.originY
            && image.pixelWidth
            && image.pixelHeight ) ) {
            return null;
        }
        const xform = proj4( `EPSG:${aerialImage.epsgCode}`, `EPSG:${image.epsgCode}` );
        const aerialPxToDatasetPx = ( ax: number, ay: number ): [number, number] => {
            const awx = aerialImage.originX + ax * aerialImage.pixelWidth;
            const awy = aerialImage.originY + ay * aerialImage.pixelHeight;
            const [dwx, dwy] = xform.forward( [awx, awy] );
            return [
                ( dwx - image.originX! ) / image.pixelWidth!,
                ( dwy - image.originY! ) / image.pixelHeight!,
            ];
        };
        const tl = aerialPxToDatasetPx( 0, 0 );
        const tr = aerialPxToDatasetPx( aerialImage.width, 0 );
        const bl = aerialPxToDatasetPx( 0, aerialImage.height );
        const br = aerialPxToDatasetPx( aerialImage.width, aerialImage.height );
        const ex = [( tr[0] - tl[0] ) / aerialImage.width, ( tr[1] - tl[1] ) / aerialImage.width];
        const ey = [( bl[0] - tl[0] ) / aerialImage.height, ( bl[1] - tl[1] ) / aerialImage.height];

        const datasetPxToAerialPx = ( dx: number, dy: number ): [number, number] => {
            const dwx = image.originX! + dx * image.pixelWidth!;
            const dwy = image.originY! + dy * image.pixelHeight!;
            const [awx, awy] = xform.inverse( [dwx, dwy] );
            return [
                ( awx - aerialImage.originX ) / aerialImage.pixelWidth,
                ( awy - aerialImage.originY ) / aerialImage.pixelHeight,
            ];
        };

        // four corners of dataset image in aerial pixel space
        const c1 = datasetPxToAerialPx( 0,           0            );
        const c2 = datasetPxToAerialPx( image.width, 0            );
        const c3 = datasetPxToAerialPx( 0,           image.height );
        const c4 = datasetPxToAerialPx( image.width, image.height );

        // axis-aligned bounding box in aerial pixel space
        const minX = Math.min( c1[0], c2[0], c3[0], c4[0] );
        const maxX = Math.max( c1[0], c2[0], c3[0], c4[0] );
        const minY = Math.min( c1[1], c2[1], c3[1], c4[1] );
        const maxY = Math.max( c1[1], c2[1], c3[1], c4[1] );        

        return new TileLayer<ImageBitmap>( {
            id: "aerial-tiles",
            pickable: false,
            tileSize: 512,
            refinementStrategy: "best-available",
            opacity: showSupport ? 1.0 : 0.0,
            minZoom: -3,
            maxZoom:  0,
            // extent: [0, 0, width, height],
            extent: [
                Math.max( 0,                    minX ),
                Math.max( 0,                    minY ),
                Math.min( aerialImage.width,    maxX ),
                Math.min( aerialImage.height,   maxY ),
            ],
            data: aerialImageTileSpec( aerialImage.aerialImageId ),
            modelMatrix: [
                ex[0], ex[1], 0, 0,    // x-axis basis in dataset pixels
                ey[0], ey[1], 0, 0,    // y-axis basis in dataset pixels
                0, 0, 1, 0,
                tl[0] + offset.x, tl[1] + offset.y, 0, 1, // origin
            ],
            renderSubLayers: props => {
                const [[left, bottom], [right, top]] = props.tile.boundingBox;
                const { data, ...otherProps } = props;
                return new BitmapLayer( otherProps, {
                    image: data,
                    bounds: [left, top, right, bottom],
                    textureParameters: {
                        minFilter: "nearest",
                        magFilter: "nearest"
                    }
                } );
            }
        } );
    }, [aerialImage, showSupport, offset] );

    const modeSlots: ( CommandCardSlot | null )[] = [
        // { key: "`", label: "Edit",  color: "white", active: drawMode?.mode === Mode.Edit         },
        // { key: "1", label: "Add",   color: "white", active: drawMode?.mode === Mode.Create       },
        { key: "2", label: "Type",   color: "white", active: drawMode?.mode === Mode.VehicleType    },
        { key: "3", label: "Color",  color: "white", active: drawMode?.mode === Mode.VehicleColor   },
        { key: "4", label: "Parts",  color: "white", active: drawMode?.mode === Mode.VehicleParts   },
        { key: "5", label: "Extras", color: "white", active: drawMode?.mode === Mode.VehicleExtras  },
        null,
    ];

    const cmdSlots = modeFns.getCommandSlots( drawMode );
    const handleExportCsv = () => {
        const rows = [
            ["ItemId", "Color", "VehicleType", "Prediction", "Prediction Confidence", "ReviewerType"],
            ...objs.map( ann => [
                ann.obbAnnotationId,
                ann.attributes?.color       ?? "",
                ann.attributes?.vehicleType ?? "",
                predictions?.get( ann.obbAnnotationId )?.label,
                predictions?.get( ann.obbAnnotationId )?.confidence,
                ""
            ] )
        ];
        const csv = rows.map( r => r.join( "," ) ).join( "\n" );
        const blob = new Blob( [csv], { type: "text/csv" } );
        const url = URL.createObjectURL( blob );
        const a = document.createElement( "a" );
        a.href = url;
        a.download = `annotations-${image.datasetImageId}.csv`;
        a.click();
        URL.revokeObjectURL( url );
    };
    const showInMap = useCallback( ( o: ObbAnnotation ) => {
        setViewState( { ...viewState, target: [o.x * image.width, o.y * image.height], zoom: 3} )
    }, [image.width, image.height, viewState] );

    const items = useMemo( () => {
        return filtered.map( obj => (
            <MemoItem key={obj.obbAnnotationId}
                obj={obj}
                selected={selObj?.obbAnnotationId === obj.obbAnnotationId}
                setHover={setHovObj}
                onClick={setSelObj}
                showInMap={showInMap}
                thumbnail={thumbnails.get( obj.obbAnnotationId )}
                prediction={predictions?.get( obj.obbAnnotationId )} />
        ) );
    }, [selObj, filtered, setSelObj, showInMap, thumbnails, predictions] );

    const modeLayer = modeFns.getLayer ? modeFns.getLayer( drawMode, objs, ctx ) : null;
    return <div id="image-annotations-page" className="d-flex">
        {showStats && <VehicleStatsModal annotations={filtered} onClose={ () => setShowStats( false )} />}
        {selObj && <EditVehicleModal
            key={selObj.obbAnnotationId}
            image={image}
            vehicle={selObj}            
            thumbnail={thumbnails.get( selObj.obbAnnotationId )}
            onSaved={updated => {
                setObjs( prev => prev.map( a =>
                    a.obbAnnotationId === updated.obbAnnotationId ? updated : a
                ) );
                setSelObj( undefined );
            }}
            onClose={ () => {
                setSelObj( undefined );
            }} 
            prediction={predictions?.get( selObj.obbAnnotationId )} /> }
        <div id="capture-list" style={{ width: 400 }} className="border-end">
            <div className="bg-body-tertiary d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2 border-bottom border-2 border-body-tertiary">
                <div className="flex-grow-1 ps-2 fs-5">{filtered.length} Annotations</div>
                <div>
                    {drawMode?.mode === Mode.VehicleType && drawMode.setTo !== undefined && showVehicleType( drawMode.setTo )}
                </div>
                Filter by Mode<Form.Switch checked={filterByMode} onChange={ () => setFilterByMode( !filterByMode ) } />
                <Button className="p-1 px-2" onClick={ () => handleExportCsv() }>
                    <PiExport />
                </Button>
            </div>
            <ListGroup variant="flush" className="y-scroll-auto">
                {items}
            </ListGroup>
        </div>
        <div className="flex-grow-1" style={{ position: "relative", backgroundColor: "#000" }}>
            <ImageAdjustPanel value={adjust} onChange={setAdjust} />
            {/* <FontAdjustPanel value={fontAdjust} onChange={setFontAdjust} /> */}
            <CommandCard key="cmdCard" modeSlots={modeSlots} cmdSlots={cmdSlots} onSelect={ slot => {} } />
            <DeckGL
                views={new OrthographicView( { id: "ortho" } )} 
                initialViewState={viewState}
                controller={true}
                layers={[ imageLayer, polygons, previewLayer, editLayer, modeLayer, typeLabels, supportLayer ].filter( Boolean )}
                onClick={handleClick}
                onHover={handleMouseMove}
                getCursor={ ( x ) => {
                    if( hovObj !== undefined ) { return "pointer"; } 
                    if( drawMode && modeFns  ) { return modeFns.getCursor( drawMode ); }
                    return "grab";
                }}
                style={{ width: "100%", height: "100%" }}
            />
        </div>
    </div>;
}
